import Hero from "@/components/Hero";
import StatsBar from "@/components/StatsBar";
import IndustrySolutions from "@/components/IndustrySolutions";
import FeaturedCaseStudies from "@/components/FeaturedCaseStudies";
import ClientLogos from "@/components/ClientLogos";
import WhyChooseUs from "@/components/WhyChooseUs";
import Exhibitions from "@/components/Exhibitions";
import EnquiryForm from "@/components/EnquiryForm";
import BlogHighlights from "@/components/BlogHighlights";
import { motion } from "framer-motion";
import { useState, useRef, useEffect } from "react";
import { Play, ArrowRight } from "lucide-react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { PILLAR_PAGES } from "@/data/cityLocalInsights";
import usePageSEOOverride from "@/hooks/useSEOOverrides";

export default function HomePage() {
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const videoRef = useRef(null);

  usePageSEOOverride("/", {
    title: "Office Furniture Manufacturer in Mumbai | Modular Workstations & Corporate Suppliers | Ezeefit",
    description: "Office furniture manufacturer in Mumbai since 2014 — modular workstations, partitions, acoustic phone booths, executive cabins. Factory-direct, ISO 9001:2015, 10-14 day delivery.",
    canonical: "https://ezeefitofficesystems.in/",
  });

  const handlePlayVideo = () => {
    setIsVideoPlaying(true);
  };

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <Helmet>
        <meta name="description" content="Modular office furniture manufacturer in Mumbai since 2014. Workstations, chairs, partitions & turnkey corporate setups. ISO 9001:2015 — factory-direct." />
        <link rel="canonical" href="https://ezeefitofficesystems.in" />
      </Helmet>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org", "@type": "FAQPage",
        "mainEntity": [
          {"@type": "Question", "name": "What types of office furniture does Ezeefit manufacture?", "acceptedAnswer": {"@type": "Answer", "text": "Ezeefit manufactures 24+ product categories including modular workstations, executive chairs, visitor chairs, 24/7 chairs, conference tables, height adjustable desks, phone booths, meeting pods, glass partitions, filing systems, locker systems, and secure storage solutions."}},
          {"@type": "Question", "name": "How much does office furniture cost in Mumbai?", "acceptedAnswer": {"@type": "Answer", "text": "Office furniture pricing varies by product: workstations start from INR 6,000, task chairs from INR 4,000, conference tables from INR 15,000, and phone booths from INR 1,25,000. Ezeefit provides factory-direct pricing with no middlemen, offering competitive value for corporate procurement."}},
          {"@type": "Question", "name": "Does Ezeefit deliver office furniture outside Mumbai?", "acceptedAnswer": {"@type": "Answer", "text": "Yes, Ezeefit delivers and installs office furniture across India — Mumbai, Navi Mumbai, Thane, Pune, Bangalore, Delhi NCR, Hyderabad, Chennai, Ahmedabad and 50+ cities. Our pan-India logistics network ensures on-time delivery everywhere."}},
          {"@type": "Question", "name": "How long does it take to furnish a corporate office?", "acceptedAnswer": {"@type": "Answer", "text": "Standard orders deliver within 3-6 weeks. Large corporate projects (100+ workstations) take 6-12 weeks including design, manufacturing, delivery, and professional installation. Ezeefit follows a systematic 9-step process to ensure on-time delivery."}},
          {"@type": "Question", "name": "Is Ezeefit a direct manufacturer or dealer?", "acceptedAnswer": {"@type": "Answer", "text": "Ezeefit is a direct manufacturer with its own factory in Bhiwandi, Thane. We design, manufacture, and install all furniture in-house — eliminating middlemen and ensuring factory-direct pricing and consistent quality control."}},
          {"@type": "Question", "name": "Can office furniture be customized to match our brand?", "acceptedAnswer": {"@type": "Answer", "text": "Absolutely. Ezeefit offers full customization — dimensions, finishes, laminate colours, handle styles, and configurations. Our design team creates 3D layouts and shop drawings tailored to your workspace requirements and brand guidelines."}},
          {"@type": "Question", "name": "Who is the best modular office furniture manufacturer in Mumbai?", "acceptedAnswer": {"@type": "Answer", "text": "Ezeefit Office Systems is a leading modular office furniture manufacturer in Mumbai, manufacturing factory-direct from Bhiwandi (Thane) since 2014. ISO 9001:2015 certified, with 28+ industries served and 10-14 day commercial turnaround. We supply workstations, partitions, acoustic phone booths and executive cabins direct, without dealer markup."}},
          {"@type": "Question", "name": "Where are office furniture suppliers located in Mumbai?", "acceptedAnswer": {"@type": "Answer", "text": "Ezeefit's manufacturing facility is in Bhiwandi (Thane District), 35-40 km from Mumbai's main commercial districts (BKC, Andheri, Lower Parel, Worli, Powai). Direct factory-to-site shipping serves the entire Mumbai metropolitan region with same-day delivery for SME orders and 2-3 day phased delivery for large fit-outs."}},
          {"@type": "Question", "name": "What is the price range of modular cabin partitions in Mumbai?", "acceptedAnswer": {"@type": "Answer", "text": "Modular cabin partitions in Mumbai range from INR 18,000 to INR 65,000 per running metre depending on system: aluminium-framed glass partitions start lower; double-glazed acoustic systems with 35 dB sound attenuation are higher. Custom cabin tables priced separately from INR 25,000."}},
          {"@type": "Question", "name": "How much does an acoustic phone booth cost in Mumbai?", "acceptedAnswer": {"@type": "Answer", "text": "Ezeefit acoustic phone booths in Mumbai range from INR 1,25,000 (1-person solo booth, 38 dB rated) to INR 4,75,000 (6-person meeting pod). 30-50% lower than imported European equivalents (Framery, Hush) with comparable Class A acoustic performance and 10-14 day delivery vs 8-12 weeks for imports."}}
        ]
      }) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org", "@type": "WebPage",
        "name": "Ezeefit Office Systems — Modular Office Furniture Manufacturer",
        "speakable": { "@type": "SpeakableSpecification", "cssSelector": ["h1", "h2", "[data-testid='hero-description']"] },
        "url": "https://ezeefitofficesystems.in"
      }) }} />

      <Hero />

      {/* Trust-stats bar (above Industry Solutions) */}
      <StatsBar />

      {/* Industry Solutions — client-logo marquee is injected between Core and Emerging via middleSlot */}
      <IndustrySolutions middleSlot={<ClientLogos />} />

      {/* Featured Case Studies */}
      <FeaturedCaseStudies />

      {/* Why Choose Us */}
      <WhyChooseUs />

      {/* Factory-Direct Manufacturer Section — SEO: factory-direct, bulk, no middlemen */}
      <section className="py-24 bg-white border-t border-[#E2E8F0]" data-testid="factory-direct-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center"
          >
            {/* LEFT — Headline + Description */}
            <div>
              <span className="font-mono text-xs text-[#F97316] tracking-widest uppercase mb-4 block">
                Factory-Direct Manufacturer · Bhiwandi
              </span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#0F172A] leading-tight tracking-tight mb-6">
                Buy Direct from the Office Furniture Factory
              </h2>
              <p className="text-base md:text-lg text-[#475569] leading-relaxed mb-6">
                Ezeefit is a <strong className="text-[#0F172A]">direct manufacturer</strong> of modular office furniture, not a reseller. Every workstation, acoustic phone booth, partition and storage unit is built in-house at our Bhiwandi factory, then delivered and installed by our own teams — eliminating dealer markups and quality middlemen.
              </p>
              <p className="text-sm text-[#64748B] leading-relaxed mb-8">
                For corporate procurement teams sourcing 50 to 1,000+ seats, factory-direct pricing typically saves <strong>15–30%</strong> versus dealer or distributor channels — without compromising on ISO 9001:2015 quality or IGBC-certified material standards.
              </p>
              <Link
                to="/contact"
                data-testid="factory-direct-cta"
                className="inline-flex items-center gap-2 px-7 py-4 bg-[#F97316] hover:bg-[#EA580C] text-white text-xs md:text-sm font-semibold uppercase tracking-wider transition-colors"
              >
                Request a Factory-Direct Quote
                <ArrowRight className="w-4 h-4" strokeWidth={2.25} />
              </Link>
            </div>

            {/* RIGHT — 3 Trust Pillars */}
            <div className="grid grid-cols-1 gap-4">
              {[
                { eyebrow: "Quality Standard", title: "ISO 9001:2015 Certified", body: "Every batch inspected against the signed BOM — dimensions, finish, hardware torque, drawer alignment, electrical components." },
                { eyebrow: "Sustainability", title: "IGBC Certificate of Compliance", body: "Low-VOC, recycled-content materials qualifying client buildings for LEED v4 ID+C and IGBC Green Interiors credits." },
                { eyebrow: "Manufacturing", title: "Own Factory in Bhiwandi", body: "Automated CNC borers, edge banders, panel saws and laminate press — 10–14 days production for a 100-seat office." },
              ].map((pillar, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1, duration: 0.5 }}
                  className="p-6 bg-[#F8FAFC] border border-[#E2E8F0] hover:border-[#F97316] transition-colors"
                  data-testid={`factory-direct-pillar-${i}`}
                >
                  <span className="font-mono text-[10px] tracking-widest uppercase text-[#F97316] mb-2 block">{pillar.eyebrow}</span>
                  <h3 className="text-base font-bold text-[#0F172A] mb-2">{pillar.title}</h3>
                  <p className="text-sm text-[#64748B] leading-relaxed">{pillar.body}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Execution Visual Proof - Cinematic Video */}
      <section className="py-24 bg-[#0F172A] relative overflow-hidden" data-testid="execution-proof-section">
        <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.4\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")' }} />
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <span className="font-mono text-sm text-[#F97316] tracking-wider uppercase mb-4 block">
              Execution Excellence
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-3">
              See Our Work in Action
            </h2>
            <p className="text-[#94A3B8] text-base max-w-2xl mx-auto">
              Where Industrial Strength Meets Corporate Elegance
            </p>
          </motion.div>

          {/* Cinematic Video Player */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative max-w-5xl mx-auto"
            data-testid="video-player-trigger"
          >
            {/* 16:9 Aspect Ratio Container */}
            <div className="relative aspect-video overflow-hidden border border-white/10">
              {/* Custom Thumbnail - shown when not playing */}
              {!isVideoPlaying && (
                <>
                  <img src="https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/8399y89k_Gemini_Generated_Image_eenxp0eenxp0eenx.png"
                    alt="Ezeefit office furniture factory in Bhiwandi, Thane — automated CNC modular office workstations manufacturing facility serving Mumbai and pan-India"
                    className="w-full h-full object-cover cursor-pointer"
                    onClick={handlePlayVideo} loading="lazy" decoding="async" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/10 pointer-events-none" />
                  <div className="absolute inset-0 flex items-center justify-center cursor-pointer" onClick={handlePlayVideo}>
                    <div className="w-20 h-20 md:w-24 md:h-24 bg-[#F97316] flex items-center justify-center hover:scale-110 transition-transform duration-300 shadow-2xl shadow-orange-500/30">
                      <Play className="w-8 h-8 md:w-10 md:h-10 text-white fill-white ml-1" />
                    </div>
                  </div>
                  <div className="absolute top-6 left-6 pointer-events-none">
                    <span className="bg-[#F97316] text-white text-[10px] font-bold tracking-widest uppercase px-3 py-1.5">
                      Virtual Factory Tour
                    </span>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8 pointer-events-none">
                    <h3 className="text-white text-lg md:text-2xl font-bold mb-1">
                      Take a Virtual Tour of Our Manufacturing Process
                    </h3>
                    <p className="text-white/70 text-sm md:text-base">
                      A decade of manufacturing excellence. Thousands of workstations. One standard of precision.
                    </p>
                  </div>
                </>
              )}

              {/* Inline Video - shown when playing */}
              {isVideoPlaying && (
                <video
                  ref={videoRef}
                  src="https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/n2rau2m5_The%20Machine%20Heart.mp4"
                  className="w-full h-full object-contain bg-black"
                  autoPlay
                  controls
                  playsInline
                  data-testid="video-player"
                />
              )}
            </div>
          </motion.div>

          {/* Trust Text */}
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="text-center text-[#94A3B8] text-sm md:text-base mt-8 max-w-3xl mx-auto italic"
          >
            "From our precision manufacturing hub in Mumbai to your corporate doorstep — we control every millimeter of the process."
          </motion.p>

          {/* Voiceover Highlights */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="grid md:grid-cols-3 gap-6 mt-14 max-w-5xl mx-auto"
          >
            {[
              { num: "01", title: "Digital Precision", desc: "Every project begins with detailed CAD drawings and 3D visualization before a single cut is made." },
              { num: "02", title: "Machine Heart", desc: "Inside our Mumbai facility, we don't just build furniture — we engineer productivity with CNC precision." },
              { num: "03", title: "The Grand Reveal", desc: "From bespoke executive cabins to high-density workstations, we translate your architectural vision into reality." },
            ].map((item) => (
              <div key={item.num} className="border border-white/10 p-6">
                <span className="font-mono text-[#F97316] text-xs tracking-wider">{item.num}</span>
                <h4 className="text-white font-semibold mt-2 mb-2">{item.title}</h4>
                <p className="text-[#64748B] text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Exhibitions */}
      <Exhibitions />

      {/* Blog Highlights - Latest Insights */}
      <BlogHighlights />

      {/* Knowledge Library — links to advisory pillar pages (GEO internal link hub) */}
      <section className="py-24 bg-white border-t border-[#E2E8F0]" data-testid="knowledge-library-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12 max-w-3xl"
          >
            <span className="font-mono text-xs text-[#F97316] tracking-widest uppercase mb-3 block">Knowledge Library</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#0F172A] tracking-tight mb-4">
              Technical Guides for Corporate Office Buyers
            </h2>
            <p className="text-[#64748B] text-base leading-relaxed">
              Free reference material from our project team — sq ft per seat benchmarks, end-to-end installation timelines and IGBC-compliant material specs. Used by facility managers, architects and procurement teams.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-5">
            {PILLAR_PAGES.map((p, i) => (
              <motion.div
                key={p.slug}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.5 }}
              >
                <Link
                  to={`/${p.slug}`}
                  data-testid={`home-pillar-${p.slug}`}
                  className="group block p-8 bg-[#F8FAFC] border border-[#E2E8F0] hover:border-[#F97316] hover:bg-white transition-all duration-300 h-full"
                >
                  <span className="font-mono text-[10px] tracking-widest uppercase text-[#F97316] mb-4 block">{p.eyebrow}</span>
                  <h3 className="text-lg font-bold text-[#0F172A] mb-3 group-hover:text-[#F97316] transition-colors leading-snug">{p.title}</h3>
                  <p className="text-sm text-[#64748B] leading-relaxed mb-6">{p.blurb}</p>
                  <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#F97316] uppercase tracking-wider">
                    Read the guide <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                  </span>
                </Link>
              </motion.div>
            ))}
          </div>

          {/* Section-level CTA — convert readers into project enquiries */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3, duration: 0.5 }}
            className="mt-14 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 p-8 bg-[#0F172A] noise-overlay"
            data-testid="knowledge-library-cta"
          >
            <div className="max-w-2xl relative z-10">
              <h3 className="text-xl md:text-2xl font-bold text-white tracking-tight mb-2">
                Have a specific project in mind?
              </h3>
              <p className="text-sm text-[#94A3B8] leading-relaxed">
                Our project team builds a free CAD layout, BOM and end-to-end timeline within one business day. No obligation, no template responses — written by someone who has executed your seat-count before.
              </p>
            </div>
            <Link
              to="/contact"
              data-testid="knowledge-library-cta-link"
              className="relative z-10 shrink-0 inline-flex items-center justify-center gap-2 px-7 py-4 bg-[#F97316] hover:bg-[#EA580C] text-white text-xs md:text-sm font-semibold uppercase tracking-wider transition-colors"
            >
              Talk to a Project Consultant
              <ArrowRight className="w-4 h-4" strokeWidth={2.25} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* CTA Section with Enquiry Form */}
      <section className="py-24 bg-[#0F172A] noise-overlay" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="font-mono text-sm text-[#F97316] tracking-wider uppercase mb-4 block">
                Ready to Transform?
              </span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
                Let's Discuss Your Project
              </h2>
              <p className="text-lg text-[#94A3B8] mb-8">
                Whether you're expanding, relocating, or setting up a new office, 
                our team is ready to help you create the perfect workspace.
              </p>
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#F97316] flex items-center justify-center">
                    <span className="text-white font-bold">1</span>
                  </div>
                  <span className="text-white">Share your requirements</span>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#F97316] flex items-center justify-center">
                    <span className="text-white font-bold">2</span>
                  </div>
                  <span className="text-white">Get a detailed consultation</span>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#F97316] flex items-center justify-center">
                    <span className="text-white font-bold">3</span>
                  </div>
                  <span className="text-white">Receive a customized proposal</span>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-white p-8 md:p-10"
            >
              <h3 className="text-2xl font-bold text-[#0F172A] mb-6">
                Corporate Project Enquiry
              </h3>
              <EnquiryForm compact />
            </motion.div>
          </div>
        </div>
      </section>


    </div>
  );
}
